import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CategoriasAsignadas } from "@/components/CategoriasAsignadas";
import { toast } from "sonner";
import { TAM_LBL, TAMANOS, ExportBar } from "@/components/bases/BasesComun";

const COLS = [["madera_recibida", "Madera recibida"], ["madera_utilizada", "Madera utilizada"], ["madera_disponible", "Madera disponible"],
  ["bases_creadas", "Bases creadas"], ["bases_salidas", "Salidas (fabricado)"], ["bases_en_stock", "En existencia"], ["bases_reservadas", "Bases reservadas"],
  ["bases_disponibles", "Bases disponibles"], ["faltantes", "Faltantes por fabricar"]];

const Movimiento = ({ isAdmin, onDone }) => {
  const [f, setF] = useState({ tipo: "madera", tamano: "IMPERIAL", cantidad: "", nota: "" });
  const save = async () => {
    try { await api.post("/bases/movimientos", { ...f, cantidad: parseFloat(f.cantidad) }); toast.success("Registrado"); setF({ ...f, cantidad: "", nota: "" }); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const sel = "h-9 rounded border border-zinc-300 px-2 text-sm bg-white";
  return (
    <div className="flex flex-wrap items-end gap-2 border border-zinc-200 rounded-md bg-white p-3" data-testid="bases-mov-form">
      <select className={sel} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} data-testid="bases-mov-tipo">
        <option value="madera">Madera recibida</option>
        {isAdmin && <option value="madera_ajuste">Ajuste de madera (+/−)</option>}
        {isAdmin && <option value="bases_ajuste">Ajuste / inventario inicial de bases (+/−)</option>}
      </select>
      <select className={sel} value={f.tamano} onChange={(e) => setF({ ...f, tamano: e.target.value })} data-testid="bases-mov-tamano">
        {TAMANOS.map((t) => <option key={t} value={t}>{TAM_LBL[t]}</option>)}
      </select>
      <Input type="number" step="0.5" className="w-28" placeholder="Cantidad" value={f.cantidad} onChange={(e) => setF({ ...f, cantidad: e.target.value })} data-testid="bases-mov-cantidad" />
      <Input className="w-56" placeholder="Nota (proveedor, motivo…)" value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} data-testid="bases-mov-nota" />
      <Button className="h-9 bg-[#002FA7]" onClick={save} disabled={!f.cantidad} data-testid="bases-mov-save">Registrar</Button>
    </div>
  );
};

const Config = ({ onSaved }) => {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/bases/config").then(({ data }) => setCfg(data));
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const saveCats = async () => {
    try { await api.put("/bases/config", { categorias: cfg.categorias }); toast.success("Categorías guardadas"); load(); onSaved(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveConsumo = async (p, v) => {
    try { await api.put("/bases/consumo", { product_id: p.id, juegos: parseFloat(v) }); toast.success(`Consumo de ${p.nombre} guardado`); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="bases-config">
        <CategoriasAsignadas value={cfg.categorias} onChange={(c) => setCfg({ ...cfg, categorias: c })} title="Categorías que llevan base" hint="Los productos de estas categorías reservan bases y, si son tamaño o producto especial, generan solicitud automática." />
        <Button onClick={saveCats} className="mt-3 h-9 bg-[#002FA7]" data-testid="bases-config-save">Guardar</Button>
      </div>
      <div className="border border-zinc-200 rounded-md bg-white p-4 max-h-80 overflow-y-auto" data-testid="bases-consumo">
        <div className="font-semibold text-sm mb-2">Consumo de madera por modelo (juegos por base)</div>
        {cfg.productos.length === 0 && <div className="text-xs text-zinc-400">Seleccione primero las categorías.</div>}
        {cfg.productos.map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-1 text-xs">
            <span className="flex-1">{p.nombre}</span>
            <Input type="number" step="0.25" min="0.25" defaultValue={p.juegos} className="w-20 h-8" onBlur={(e) => Number(e.target.value) !== p.juegos && saveConsumo(p, e.target.value)} data-testid={`bases-consumo-${p.id}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const BasesInventario = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const load = () => api.get("/bases/inventario").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-zinc-500">Cargando…</div>;
  const falt = d.reservas.filter((r) => r.faltante > 0 && !r.especial);
  return (
    <div className="space-y-5" data-testid="bases-inventario">
      <div className="flex justify-end"><ExportBar seccion="inventario" /></div>
      <div className="overflow-x-auto border border-zinc-200 rounded-md bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500"><tr><th className="px-3 py-2 text-left">Tamaño</th>{COLS.map(([, l]) => <th key={l} className="px-3 py-2 text-right">{l}</th>)}</tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {d.inventario.map((r) => (
              <tr key={r.tamano} data-testid={`bases-inv-${r.tamano}`}>
                <td className="px-3 py-2 font-semibold">{TAM_LBL[r.tamano]}</td>
                {COLS.map(([k]) => <td key={k} className={`px-3 py-2 text-right font-mono ${k === "bases_disponibles" ? "font-bold" : ""} ${(k === "faltantes" && r[k] > 0) || r[k] < 0 ? "text-red-600 font-bold" : ""}`} data-testid={`bases-inv-${r.tamano}-${k}`}>{r[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Movimiento isAdmin={isAdmin} onDone={load} />
      <div>
        <div className="font-semibold text-sm mb-2">Pedidos con faltantes ({falt.length})</div>
        <div className="overflow-x-auto border border-zinc-200 rounded-md bg-white max-h-72" data-testid="bases-faltantes">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500"><tr>{["Pedido", "Cliente", "Producto", "Tamaño", "Cant.", "Reservadas", "Faltan"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-zinc-100">
              {falt.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-zinc-400">Todos los pedidos tienen sus bases reservadas.</td></tr>}
              {falt.map((r) => <tr key={`${r.order_id}-${r.key}`} className={r.prioridad ? "bg-red-100" : ""}><td className="px-3 py-1.5 font-mono">{r.pedido}</td><td className="px-3 py-1.5">{r.cliente}</td><td className="px-3 py-1.5">{r.producto}</td><td className="px-3 py-1.5">{r.tamano}</td><td className="px-3 py-1.5">{r.cantidad}</td><td className="px-3 py-1.5">{r.reservadas}</td><td className="px-3 py-1.5 font-bold text-red-600">{r.faltante}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Últimos movimientos</div>
        <div className="border border-zinc-200 rounded-md bg-white max-h-56 overflow-y-auto text-xs divide-y divide-zinc-100" data-testid="bases-movimientos">
          {d.movimientos.length === 0 && <div className="p-3 text-zinc-400">Sin movimientos.</div>}
          {d.movimientos.map((m) => <div key={m.id} className="px-3 py-1.5 flex gap-3"><span className="text-zinc-500 w-36">{m.fecha}</span><span className="w-48">{{ madera: "Madera recibida", madera_ajuste: "Ajuste madera", bases_ajuste: "Ajuste bases" }[m.tipo]}</span><span className="w-24">{TAM_LBL[m.tamano]}</span><span className="font-mono w-16">{m.cantidad}</span><span className="flex-1 text-zinc-600">{m.nota} · {m.by}</span></div>)}
        </div>
      </div>
      {isAdmin && <Config onSaved={load} />}
    </div>
  );
};
